import React from 'react';
import { InfoTooltip } from './InfoTooltip';
import './DrillDownRow.scss';

interface DrillDownRowProps {
  stageName: string;
  stageColor: string;
  summaryText: React.ReactNode;
  bar: React.ReactNode;
  /**
   * What this row is, in a sentence, revealed from an icon beside the name. Absent or null renders no icon
   * at all — an icon that opened an empty bubble cannot be told apart from one that failed.
   */
  description?: string | null;
  expanded: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}

export const DrillDownRow: React.FC<DrillDownRowProps> = ({
  stageName,
  stageColor,
  summaryText,
  bar,
  description,
  expanded,
  onToggle,
  children,
}) => (
  <div className={`ovp-drill ${expanded ? 'ovp-drill--open' : ''}`}>
    {/* Head line and bar are one click target, matching the mock. Focusable, so it must also activate
        from the keyboard — Space is prevented from scrolling the page first. */}
    <div
      className="ovp-drill__row"
      onClick={onToggle}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      }}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
    >
      <div className="ovp-drill__head">
        {/* One glyph rotated 90° when open, rather than two swapped glyphs — the rotation animates and
            reads as one control changing state. */}
        <span className="ovp-drill__caret">▶</span>
        <span className="ovp-drill__dot" style={{ background: stageColor }} />
        {/* Name and icon are one group, so the icon sits a word-space from the word it explains rather than
            at the head line's own 8px rhythm, which is there to separate the row's independent parts. It is
            inside the row's click target — the tooltip stops its own click and keystrokes so reading about a
            stage does not expand it. */}
        <span className="ovp-drill__label">
          <span className="ovp-drill__name">{stageName}</span>
          {description && <InfoTooltip about={stageName} text={description} />}
        </span>
        <span className="ovp-drill__summary">{summaryText}</span>
      </div>
      <div className="ovp-drill__bar">{bar}</div>
    </div>
    {expanded && children && <div className="ovp-drill__detail">{children}</div>}
  </div>
);
